'use client';
import React, { useEffect, useState } from 'react';

export default function HeroSection() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 80);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative bg-gray-950 overflow-hidden min-h-screen flex items-center">
      {/* Full-bleed background photo */}
      <div
        className="absolute inset-0 bg-cover bg-center transition-transform duration-[8000ms] ease-out"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=1800&q=80&auto=format&fit=crop')`,
          transform: loaded ? 'scale(1.04)' : 'scale(1.12)',
        }}
      />
      {/* Dark overlay — heavy on left, lighter right */}
      <div className="absolute inset-0 bg-gradient-to-r from-gray-950/95 via-gray-950/70 to-gray-950/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-gray-950/80 via-transparent to-transparent" />
      {/* Decorative accent line */}
      <div
        className="absolute left-0 top-0 h-full w-1 bg-brand transition-all duration-1000 ease-out"
        style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'scaleY(1)' : 'scaleY(0)', transformOrigin: 'top' }}
      />
      {/* Content */}
      <div className="relative z-10 max-w-screen-xl mx-auto px-6 md:px-16 py-24 md:py-36 w-full">
        {/* Eyebrow */}
        <div
          className="inline-flex items-center gap-2 mb-8 transition-all duration-700 ease-out"
          style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(24px)' }}
        >
          <span className="w-8 h-px bg-brand" />
          <span className="text-brand text-xs font-bold uppercase tracking-[0.25em]">Rijschool in Almere</span>
        </div>

        {/* Giant headline */}
        <div className="overflow-hidden mb-2">
          <h1
            className="text-white font-black leading-none transition-all duration-700 ease-out delay-100"
            style={{
              fontSize: 'clamp(3rem, 9vw, 8rem)',
              opacity: loaded ? 1 : 0,
              transform: loaded ? 'translateY(0)' : 'translateY(100%)',
            }}
          >
            Jouw rijbewijs,
          </h1>
        </div>
        <div className="overflow-hidden mb-10">
          <h1
            className="font-black leading-none transition-all duration-700 ease-out delay-200"
            style={{
              fontSize: 'clamp(3rem, 9vw, 8rem)',
              color: 'transparent',
              WebkitTextStroke: '2px #D4AF37',
              opacity: loaded ? 1 : 0,
              transform: loaded ? 'translateY(0)' : 'translateY(100%)',
            }}
          >
            op jouw tempo.
          </h1>
        </div>

        {/* Sub + CTA row */}
        <div
          className="flex flex-col md:flex-row md:items-end gap-8 md:gap-16 transition-all duration-700 ease-out delay-300"
          style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(32px)' }}
        >
          <p className="text-gray-300 text-lg md:text-xl leading-relaxed max-w-sm">
            Persoonlijke rijlessen met een vaste instructeur. Flexibel, betaalbaar en met hoge slagingskans.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <a
              href="https://driveplannen.nl/registreer/46ec6865-5dc5-4e1c-a646-ce961abc1633"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative overflow-hidden bg-brand text-white font-bold px-8 py-4 text-sm uppercase tracking-widest hover:bg-brand/90 active:scale-95 transition-all duration-200 text-center"
            >
              <span className="relative z-10">Schrijf je gratis in</span>
            </a>
            <a
              href="/tarieven"
              className="border border-white/30 text-white font-bold px-8 py-4 text-sm uppercase tracking-widest hover:bg-white/10 active:scale-95 transition-all duration-200 text-center"
            >
              Bekijk tarieven
            </a>
          </div>
        </div>

        {/* Trust row */}
        <div
          className="flex flex-wrap gap-8 mt-16 pt-8 border-t border-white/10 transition-all duration-700 ease-out delay-500"
          style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(24px)' }}
        >
          {[
            { num: '⭐', label: 'Tevreden klanten, hoog beoordeeld op google reviews' },
            { num: 'Flex', label: 'Avond & weekend' },
            { num: 'App', label: 'Lessen plannen' },
          ]?.map((b) => (
            <div key={b?.label} className="flex items-center gap-3">
              <span className="text-brand font-black text-2xl">{b?.num}</span>
              <span className="text-gray-400 text-sm">{b?.label}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Scroll indicator */}
      <div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 transition-all duration-700 ease-out delay-700"
        style={{ opacity: loaded ? 1 : 0 }}
      >
        <span className="text-gray-500 text-xs uppercase tracking-widest">Scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-gray-500 to-transparent animate-pulse" />
      </div>
    </section>
  );
}
